import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../../components/common';
import { useCentroCostoStore } from '../store/centroCostoStore';
import type { CentroCostoContable, AreaContable } from '../types/centroCosto';
import { initialCentroCosto } from '../types/centroCosto';
import { CentroCostoService } from '../services/centroCostoService';

const CentrosCostoPage: React.FC = () => {
  const {
    centrosCosto,
    loading,
    error,
    selectedCentroCosto,
    dialogOpen,
    fetchCentrosCosto,
    saveCentroCosto,
    deleteCentroCosto,
    setSelectedCentroCosto,
    openDialog,
    closeDialog,
    clearError,
  } = useCentroCostoStore();

  const [formData, setFormData] = useState<Partial<CentroCostoContable>>(initialCentroCosto);
  const [areas, setAreas] = useState<AreaContable[]>([]);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [areaForm, setAreaForm] = useState<Partial<AreaContable>>({ idAreaContable: 0, codigo: '', descripcion: '', activo: true });

  useEffect(() => {
    fetchCentrosCosto();
    loadAreas();
  }, [fetchCentrosCosto]);

  const loadAreas = async () => {
    try {
      const data = await CentroCostoService.getAllAreas();
      setAreas(data);
    } catch (err) {
      // handled
    }
  };

  // Flatten for display
  const flattenItems = (items: CentroCostoContable[]): CentroCostoContable[] => {
    const result: CentroCostoContable[] = [];
    const addItems = (list: CentroCostoContable[]) => {
      for (const item of list) {
        result.push(item);
        if (item.hijos && item.hijos.length > 0) {
          addItems(item.hijos);
        }
      }
    };
    addItems(items);
    return result;
  };

  const flatItems = flattenItems(centrosCosto);

  const columns: GridColDef[] = [
    { field: 'codigo', headerName: 'Código', width: 120 },
    {
      field: 'descripcion',
      headerName: 'Descripción',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <span style={{ paddingLeft: `${(params.row.nivel - 1) * 24}px` }}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'nivel',
      headerName: 'Nivel',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          <GridEditButton
            onClick={() => handleEdit(params.row)}
            ariaLabel={`Editar centro costo ${params.row.codigo}`}
          />
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.idCentroCostoContable)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const handleEdit = (item: CentroCostoContable) => {
    setSelectedCentroCosto(item);
    setFormData(item);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedCentroCosto(null);
    setFormData({ ...initialCentroCosto });
    openDialog();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar este centro de costo?')) {
      try {
        await deleteCentroCosto(id);
      } catch (err) {
        // handled
      }
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      setFormData(selectedCentroCosto || { ...initialCentroCosto });
    }
  }, [dialogOpen, selectedCentroCosto]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await saveCentroCosto(formData);
    } catch (err) {
      // handled
    }
  };

  const handleSaveArea = async () => {
    try {
      await CentroCostoService.saveArea(areaForm as AreaContable);
      await loadAreas();
      setAreaDialogOpen(false);
    } catch (err) {
      // handled
    }
  };

  const parentOptions = flatItems.filter(c =>
    c.nivel < 3 && c.idCentroCostoContable !== formData.idCentroCostoContable
  );

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Centros de Costo"
        subtitle="Gestión de centros de costo y áreas contables"
        actions={
          <AddButton text="Nuevo Centro de Costo" onClick={handleCreate} />
        }
      />

      {loading && <CircularProgress />}
      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <DataGrid
          columns={columns}
          rows={flatItems}
          getRowId={(row: CentroCostoContable) => row.idCentroCostoContable}
        />
      )}

      {/* Centro de Costo Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCentroCosto ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Código"
                value={formData.codigo || ''}
                onChange={(e) => handleFormChange('codigo', e.target.value)}
                inputProps={{ maxLength: 10 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nivel"
                type="number"
                value={formData.nivel || 1}
                onChange={(e) => handleFormChange('nivel', parseInt(e.target.value))}
                inputProps={{ min: 1, max: 3 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descripción"
                value={formData.descripcion || ''}
                onChange={(e) => handleFormChange('descripcion', e.target.value)}
                inputProps={{ maxLength: 200 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={parentOptions}
                getOptionLabel={(opt) => `${opt.codigo} - ${opt.descripcion}`}
                value={parentOptions.find(p => p.idCentroCostoContable === formData.idPadre) || null}
                onChange={(_e, newVal) => {
                  handleFormChange('idPadre', newVal?.idCentroCostoContable || null);
                  handleFormChange('codigoPadre', newVal?.codigo || null);
                }}
                renderInput={(params) => <TextField {...params} label="Centro de Costo Padre" />}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.activo !== false}
                    onChange={(e) => handleFormChange('activo', e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </ListLayoutPanel>
  );
};

export default CentrosCostoPage;
